import cn from 'classnames';
import { isValidElement, useContext, useEffect, useMemo, useRef, useState } from 'react';

import { CheckMark } from '@webapp/common/assets/icons';
import { CustomStylesCtx } from '@webapp/common/components/custom-styles';
import { CssUiComponent } from '@webapp/common/components/survey-custom';
import { setHtml } from '@webapp/common/lib/ui';
import { nop } from '@webapp/common/lib/utils';

import css from './checkbox.css';

export const Checkbox: FC<{
    label?: ReactNode;
    labelClassName?: string;
    className?: string;
    defaultChecked?: boolean;
    checked?: boolean;
    disabled?: boolean;
    round?: boolean;
    style?: CSSProperties;
    onChange?: (checked: boolean) => void;
}> = ({ onChange = nop, defaultChecked, checked, className, disabled, label, labelClassName, round }) => {
    const [innerChecked, setChecked] = useState<boolean>(defaultChecked || checked || false);
    const prev = useRef({
        checked,
        defaultChecked,
    });
    const {
        checkbox: { checked: checkedStyle, label: labelStyle, root },
    } = useContext(CustomStylesCtx);

    const handleChange = (): void => {
        const next = !innerChecked;

        if (!disabled) {
            setChecked(next);
            onChange(next);
        }
    };

    useEffect(() => {
        if (prev.current.checked !== checked) {
            prev.current.checked = checked;
            setChecked(checked);
        } else if (prev.current.defaultChecked !== defaultChecked) {
            prev.current.defaultChecked = defaultChecked;
            setChecked(defaultChecked);
        }
    }, [checked, defaultChecked]);

    const style = useMemo(
        () =>
            Object.assign(
                {},
                root,
                innerChecked ? checkedStyle : {},
                round ? {} : { width: checkedStyle?.height || root?.height },
                { fontSize: labelStyle.fontSize },
            ),
        [checkedStyle, innerChecked, labelStyle.fontSize, root, round],
    );

    const markStyle = useMemo(
        () => ({
            color: checkedStyle?.color,
        }),
        [checkedStyle?.color],
    );

    return (
        <div className={cn(css.checkbox, CssUiComponent.CHECKBOX, className)} onClick={handleChange}>
            <div
                style={style}
                className={cn(css.control, CssUiComponent.CHECKBOX_MARK, {
                    [css.round]: round,
                    [css.disabled]: disabled,
                    [css.checked]: innerChecked,
                    [CssUiComponent.CHECKBOX_SELECTED]: innerChecked,
                })}
            >
                <CheckMark className={cn({ [css.visible]: innerChecked })} style={markStyle} />
            </div>

            {label &&
                (isValidElement(label) ? (
                    <div className={cn(css.label, labelClassName)} style={labelStyle}>
                        {label}
                    </div>
                ) : (
                    <div className={cn(css.label, labelClassName)} {...setHtml(String(label))} style={labelStyle} />
                ))}
        </div>
    );
};
Checkbox.displayName = 'Checkbox';
